/**
 * MarginFloorAudit.jsx (iter143)
 *
 * Friday-afternoon margin review: lists every (SKU × tier) whose effective
 * price sits below — or within `buffer_pp` of — the tenant's configured
 * margin floor. Sorted most-breached first, with status chips, override
 * badges, and quick-link to the cell in Customer Pricing.
 *
 * Empty states:
 *   • paywall — when the customer_pricing addon is not active (FeatureGate)
 *   • setup CTA — when the tenant has not yet configured a floor
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Shield, Warning, ArrowClockwise, Lock, MagnifyingGlass, ArrowSquareOut,
  CaretLeft, CaretRight, Funnel,
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 100;

const fmtMoney = (n, currency = 'USD') => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—';
  try {
    return Number(n).toLocaleString('en-US', {
      style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2,
    });
  } catch {
    return `$${Number(n).toFixed(2)}`;
  }
};

const statusChip = (status, distance) => {
  if (status === 'below_floor') {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 border bg-rose-50 text-rose-700 border-rose-200 uppercase tracking-widest font-bold">
        <Warning size={10} weight="fill" /> Below {distance.toFixed(1)}pp
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 border bg-amber-50 text-amber-700 border-amber-200 uppercase tracking-widest font-bold">
      Near +{distance.toFixed(1)}pp
    </span>
  );
};

export default function MarginFloorAudit() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [onlyBreaches, setOnlyBreaches] = useState(false);
  const [bufferPp, setBufferPp] = useState(5);
  const [page, setPage] = useState(1);
  const [categories, setCategories] = useState([]);

  const load = async (p = page) => {
    setLoading(true);
    try {
      const params = {
        page: p,
        page_size: PAGE_SIZE,
        buffer_pp: bufferPp,
        only_breaches: onlyBreaches,
      };
      if (tierFilter) params.tier_key = tierFilter;
      if (categoryFilter) params.category = categoryFilter;
      const r = await axios.get(`${API}/api/customer-pricing/margin-floor-audit`, {
        params, withCredentials: true,
      });
      setData(r.data);
      setError('');
    } catch (e) {
      if (e?.response?.status === 402) setError('upgrade_required');
      else setError(e?.response?.data?.detail || 'Could not load audit');
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const r = await axios.get(`${API}/api/customer-pricing/categories`, { withCredentials: true });
      setCategories(r.data || []);
    } catch { /* swallow */ }
  };

  useEffect(() => { loadCategories(); }, []);
  useEffect(() => { load(1); setPage(1); /* eslint-disable-next-line */ }, [tierFilter, categoryFilter, onlyBreaches, bufferPp]);
  useEffect(() => { load(page); /* eslint-disable-next-line */ }, [page]);

  const totalPages = useMemo(
    () => (data ? Math.max(1, Math.ceil((data.total || 0) / PAGE_SIZE)) : 1),
    [data],
  );

  if (error === 'upgrade_required') {
    return (
      <Layout>
        <div className="p-12 text-center max-w-lg mx-auto" data-testid="floor-audit-paywall">
          <Lock size={48} className="text-amber-600 mx-auto mb-4" weight="fill" />
          <h1 className="text-2xl font-bold mb-2">Margin Floor Audit</h1>
          <p className="text-sm text-muted-foreground mb-6">
            Part of the <b>Custom Customer Price List (A$149/mo)</b> add-on or the
            <b> Revenue Operations Suite bundle (A$399/mo)</b>.
          </p>
          <a href="/settings/billing/addons" data-testid="floor-audit-upgrade-btn">
            <Button className="bg-brand hover:bg-brand/90 text-white rounded-none">View add-ons</Button>
          </a>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="margin-floor-audit-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
              <Shield size={28} weight="fill" className="text-amber-600" /> Margin Floor Audit
            </h1>
            <p className="text-muted-foreground mt-1 text-sm">
              Every SKU × tier whose effective margin breaches — or sits within a buffer of — your configured floor.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => load(page)} className="rounded-none" data-testid="audit-refresh">
              <ArrowClockwise size={14} className="mr-2" /> Refresh
            </Button>
          </div>
        </div>

        {/* Setup CTA when floor not configured */}
        {data && data.floor_disabled && (
          <div className="bg-amber-50 border border-amber-200 p-6 flex items-start gap-4" data-testid="audit-setup-cta">
            <Shield size={28} weight="fill" className="text-amber-600 shrink-0" />
            <div className="flex-1">
              <div className="font-bold text-sm mb-1">No margin floor configured yet</div>
              <p className="text-xs text-muted-foreground mb-3 leading-relaxed">
                Set a minimum margin % in Customer Pricing — Customer Price List → Margin floor protection — and we&apos;ll start surfacing breaches here. Suggested starting floor is 15-25% for distributors / 30-40% for retail.
              </p>
              <Button
                onClick={() => navigate('/settings/customer-pricing')}
                className="bg-brand hover:bg-brand/90 text-white rounded-none"
                data-testid="audit-configure-floor-btn"
              >
                Configure floor →
              </Button>
            </div>
          </div>
        )}

        {/* KPI tiles */}
        {data && !data.floor_disabled && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="audit-kpis">
            <div className="bg-card border border-border p-4" data-testid="kpi-floor">
              <div className="overline">Configured floor</div>
              <div className="text-3xl font-bold tabular-nums mt-1 flex items-baseline gap-1">
                {data.min_margin_pct}<span className="text-base text-muted-foreground">%</span>
              </div>
              <div className="text-[11px] text-muted-foreground mt-1">
                Enforcement: <b className="uppercase">{data.floor_enforcement}</b>
              </div>
            </div>
            <div className="bg-card border border-border p-4" data-testid="kpi-below">
              <div className="overline">SKUs below floor</div>
              <div className={`text-3xl font-bold tabular-nums mt-1 ${data.summary.below_floor > 0 ? 'text-rose-700' : 'text-emerald-700'}`}>
                {data.summary.below_floor}
              </div>
              <div className="text-[11px] text-muted-foreground mt-1">
                {data.summary.below_floor > 0 ? 'Action required' : 'All clear ✓'}
              </div>
            </div>
            <div className="bg-card border border-border p-4" data-testid="kpi-near">
              <div className="overline">Near floor (±{data.buffer_pp}pp)</div>
              <div className={`text-3xl font-bold tabular-nums mt-1 ${data.summary.near_floor > 0 ? 'text-amber-700' : 'text-muted-foreground'}`}>
                {data.summary.near_floor}
              </div>
              <div className="text-[11px] text-muted-foreground mt-1">Watch list</div>
            </div>
            <div className="bg-card border border-border p-4" data-testid="kpi-avg-margin">
              <div className="overline">Avg margin (all cells)</div>
              <div className="text-3xl font-bold tabular-nums mt-1">
                {data.summary.avg_margin == null ? '—' : `${data.summary.avg_margin}%`}
              </div>
              <div className="text-[11px] text-muted-foreground mt-1">
                {data.summary.total_rows} cells with cost basis
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        {data && !data.floor_disabled && (
          <div className="bg-card border border-border p-4 grid grid-cols-1 md:grid-cols-[auto_1fr_1fr_1fr_auto] gap-3 items-end" data-testid="audit-filters">
            <div className="flex items-center gap-2 text-sm font-bold">
              <Funnel size={16} weight="fill" className="text-brand" /> Filters
            </div>
            <div>
              <Label className="overline" htmlFor="audit-tier">Tier</Label>
              <select
                id="audit-tier"
                value={tierFilter}
                onChange={(e) => setTierFilter(e.target.value)}
                className="mt-1 w-full text-sm px-3 py-2 border border-border bg-background"
                data-testid="audit-tier-filter"
              >
                <option value="">All 4 tiers</option>
                {(data.tiers || []).map((t) => (
                  <option key={t.key} value={t.key}>{t.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="overline" htmlFor="audit-category">Category</Label>
              <select
                id="audit-category"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="mt-1 w-full text-sm px-3 py-2 border border-border bg-background"
                data-testid="audit-category-filter"
              >
                <option value="">All categories</option>
                {categories.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="overline" htmlFor="audit-buffer">Near-floor buffer (pp)</Label>
              <Input
                id="audit-buffer"
                type="number"
                step="0.5"
                min="0"
                max="50"
                value={bufferPp}
                onChange={(e) => setBufferPp(parseFloat(e.target.value) || 0)}
                className="mt-1 tabular-nums font-mono"
                data-testid="audit-buffer-input"
              />
            </div>
            <label className="flex items-center gap-2 text-xs cursor-pointer pb-2" data-testid="audit-only-breaches-label">
              <input
                type="checkbox"
                checked={onlyBreaches}
                onChange={(e) => setOnlyBreaches(e.target.checked)}
                data-testid="audit-only-breaches"
              />
              <span>Only below-floor</span>
            </label>
          </div>
        )}

        {/* Offenders table */}
        {data && !data.floor_disabled && (
          <div className="bg-card border border-border overflow-x-auto" data-testid="audit-table">
            {loading ? (
              <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
            ) : data.rows.length === 0 ? (
              <div className="p-12 text-center" data-testid="audit-empty">
                <Shield size={32} weight="fill" className="text-emerald-600 mx-auto mb-3" />
                <div className="font-bold text-base mb-1">All margins healthy ✓</div>
                <p className="text-xs text-muted-foreground">No SKUs below the {data.min_margin_pct}% floor or within ±{data.buffer_pp}pp of it.</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-muted text-xs uppercase tracking-widest text-muted-foreground">
                  <tr>
                    <th className="text-left p-3">SKU</th>
                    <th className="text-left p-3">Tier</th>
                    <th className="text-right p-3">Cost</th>
                    <th className="text-right p-3">Price</th>
                    <th className="text-right p-3">Margin</th>
                    <th className="text-left p-3">Status</th>
                    <th className="text-left p-3">Type</th>
                    <th className="text-right p-3 w-[1%]"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r, idx) => (
                    <tr
                      key={`${r.sku_id}-${r.tier_key}-${idx}`}
                      className="border-t border-border hover:bg-muted/30"
                      data-testid={`audit-row-${r.sku_code}-${r.tier_key}`}
                    >
                      <td className="p-3">
                        <div className="font-mono text-xs font-bold">{r.sku_code}</div>
                        <div className="text-[11px] text-muted-foreground truncate max-w-[260px]">{r.name}</div>
                      </td>
                      <td className="p-3 text-xs font-bold uppercase">{r.tier_name}</td>
                      <td className="p-3 text-right tabular-nums font-mono text-xs text-muted-foreground">{fmtMoney(r.landed_cost, r.tier_currency)}</td>
                      <td className="p-3 text-right tabular-nums font-mono text-xs">{fmtMoney(r.price, r.tier_currency)}</td>
                      <td className={`p-3 text-right tabular-nums font-mono text-xs font-bold ${r.status === 'below_floor' ? 'text-rose-700' : 'text-amber-700'}`}>
                        {r.margin_pct}%
                      </td>
                      <td className="p-3">{statusChip(r.status, r.distance_pp)}</td>
                      <td className="p-3 text-xs">
                        {r.is_override ? (
                          <span className="inline-flex items-center gap-1 text-brand font-bold" data-testid={`audit-override-flag-${r.sku_code}-${r.tier_key}`}>
                            <Lock size={10} weight="fill" /> Manual
                          </span>
                        ) : (
                          <span className="text-muted-foreground">Auto formula</span>
                        )}
                      </td>
                      <td className="p-3">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/settings/customer-pricing?sku=${encodeURIComponent(r.sku_code)}`)}
                          className="rounded-none h-7 text-xs"
                          data-testid={`audit-fix-${r.sku_code}-${r.tier_key}`}
                        >
                          <ArrowSquareOut size={11} className="mr-1" /> Fix
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Pagination */}
        {data && !data.floor_disabled && data.total > PAGE_SIZE && (
          <div className="flex items-center justify-end gap-2">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} className="rounded-none" data-testid="audit-prev-page">
              <CaretLeft size={12} /> Prev
            </Button>
            <span className="text-xs font-mono">{page} / {totalPages}</span>
            <Button size="sm" variant="outline" disabled={!data.has_more} onClick={() => setPage(page + 1)} className="rounded-none" data-testid="audit-next-page">
              Next <CaretRight size={12} />
            </Button>
          </div>
        )}
      </div>
    </Layout>
  );
}
